/* eslint-disable i18next/no-literal-string */
import { Box, Theme, useTheme } from "@outschool/backpack";
import { useLocale } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import React from "react";
import { CaptionLabelProps, useDayPicker } from "react-day-picker-v8";

import DayPickerLocalized from "./DayPickerLocalized";
import { convertDate } from "./helpers";

const getCSSScript = (
  theme: Theme,
  modifiersCss: string,
  usesMultipleWeekSelection: boolean,
) => {
  let css = `
    .rdp {
      --rdp-cell-size: 32px;
    }

    .rdp {
      margin: 0;
    }

    .rdp-caption > h2 {
      font-size: ${theme.typography.pxToRem(18)} !important;
      font-weight: bold;
    }

    .rdp-caption_label {
      padding: 0;
      font-size: 22px;
    }

    .rdp-head_cell, .rdp-day {
      font-size: ${theme.typography.pxToRem(14)};
      ${theme.breakpoints.up("sm")} {
        font-size: 16px;
      }
      ${theme.breakpoints.up("md")} {
        font-size: 14px;
      }
    }

    .rdp-day {
      padding: 4px !important;
      margin: 6px;
      ${theme.breakpoints.up("sm")} {
        margin-right: 24.25px;
        margin-left: 24.25px;
        margin-top: 6px;
        margin-bottom: 6px;
      }
      ${theme.breakpoints.up("md")} {
        margin: 3.75px;
      }
    }

    ${modifiersCss}

    .rdp-head_cell, .rdp-button {
      color: ${theme.palette.grey?.[600]};
    }

    .rdp-button:hover:not([disabled]),
    .rdp-button:hover:not([disabled]):not(.rdp-day_selected) {
      background-color: ${theme.palette.grey?.[100]};
      border: 2px solid ${theme.palette.primary.dark};
      color: ${theme.palette.text.primary};
    }

    .rdp-button:focus-visible:not([disabled]) {
      background-color: ${theme.palette.grey?.[100]};
      border: 2px solid ${theme.palette.primary.dark};
      color: ${theme.palette.text.primary};
      outline-offset: 2px;
      outline: 2px solid ${theme.palette.primary.dark};
    }

    .rdp-day_selected {
      border: 2px solid ${theme.palette.primary.dark} !important;
      background-color: ${theme.palette.primary.dark} !important;
      color: white !important;
    }
  `;

  if (usesMultipleWeekSelection) {
    css += `
      .rdp-day_selected.unavailable {
        background-color: transparent !important;
        border: none !important;
        color: ${theme.palette.grey?.[500]} !important;
      }
      .rdp-day_selected.unavailable:before {
        position: absolute;
        content: "";
        width: 1px;
        margin-top: 1px;
        height: 55%;
        box-shadow: 0px 0px 0px 1px ${theme.palette.grey?.[50]};
        background-color: ${theme.palette.grey?.[500]} !important;
        outline: none;
        
        -webkit-transform:rotate(-45deg);
        -moz-transform:rotate(-45deg);
        -ms-transform:rotate(-45deg);
        -o-transform:rotate(-45deg);
        transform:rotate(-45deg);
      }
    `;
  }

  return css;
};

export default function AvailableDateSelector({
  availableDates,
  today,
  defaultMonth,
  firstAvailableDate,
  selectedDate,
  timeZone,
  startOfDateString,
  handleDayClick,
  handleMonthChange,
  numWeeksToRepeat,
  enrollableTimesByDate,
  hasSelectedTime,
  enrollmentDuration_sessions,
}: {
  availableDates: string[];
  today: Date;
  defaultMonth?: Date;
  firstAvailableDate?: Date;
  selectedDate: Date;
  timeZone: string;
  startOfDateString: (date: Date) => string;
  handleDayClick: (date: Date) => void;
  handleMonthChange?: (date: Date) => void;
  numWeeksToRepeat?: number | null;
  enrollableTimesByDate?: Record<string, string[]>;
  hasSelectedTime?: boolean;
  enrollmentDuration_sessions?: number | null;
}) {
  const locale = useLocale();
  const theme: any = useTheme();
  const [computedSelectedDates, setComputedSelectedDates] = React.useState<
    Date[]
  >([]);

  const setSelectedDates = (
    selectedDate: Date,
    timeZone: string,
    numWeeksToRepeat?: number | null,
  ) => {
    const selectedDates: Date[] = [];

    /**
     * An undefined value means we are only selecting one date.
     * Null means we want to continue to schedule until the user cancels.
     * We can't select an unlimited number of dates, so we schedule out to 1 year.
     */
    let numDatesToSelect;
    if (numWeeksToRepeat === undefined) {
      numDatesToSelect = 1;
    } else if (numWeeksToRepeat === null) {
      numDatesToSelect = 52;
    } else {
      numDatesToSelect = numWeeksToRepeat;
    }

    for (let weeksToAdd = 0; weeksToAdd < numDatesToSelect; weeksToAdd++) {
      const nextDate = dayjs(selectedDate).add(weeksToAdd, "weeks").toDate();
      // If we are allowing the user to select multiple weeks at a time, we should
      // check if the selected time is available for that date before marking it as selected.
      if (
        numDatesToSelect !== 1 &&
        hasSelectedTime &&
        enrollableTimesByDate &&
        enrollableTimesByDate[startOfDateString(nextDate)]?.length
      ) {
        const availableTimesForNextDate =
          enrollableTimesByDate[startOfDateString(nextDate)];
        const nextDateString = dayjs(nextDate).tz(timeZone).toISOString();

        if (availableTimesForNextDate.includes(nextDateString)) {
          selectedDates.push(convertDate(nextDate, timeZone));
        }
      } else {
        selectedDates.push(convertDate(nextDate, timeZone));
      }
    }
    setComputedSelectedDates(selectedDates);
  };

  React.useEffect(() => {
    if (enrollmentDuration_sessions) {
      setSelectedDates(selectedDate, timeZone, enrollmentDuration_sessions);
    } else {
      setSelectedDates(selectedDate, timeZone, numWeeksToRepeat);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, numWeeksToRepeat, timeZone, enrollmentDuration_sessions]);

  const convertedAvailableDates = availableDates.map(date =>
    startOfDateString(convertDate(date, timeZone)),
  );

  const isDateAvailable = React.useCallback(
    (date: Date) => convertedAvailableDates.includes(startOfDateString(date)),
    [convertedAvailableDates, startOfDateString],
  );

  const lastAvailableDate: Date =
    dayjs.max(convertedAvailableDates.map(date => dayjs(date)))?.toDate() ??
    undefined;

  const modifierArr = [
    {
      className: "today",
      selector: today,
      css: `.today {
        border: 2px solid ${theme.palette.primary?.[200]};
        background-color: ${theme.palette.grey?.[50]};
      }`,
    },
    {
      className: "disabled",
      selector: { before: convertDate(today, timeZone) },
      css: `.disabled {
        color: ${theme.palette.grey?.[500]};
        text-decoration: line-through;
      }`,
    },
    {
      className: "available",
      selector: isDateAvailable,
      css: `.available {
        color: ${theme.palette.grey?.[900]};
        background-color: ${theme.palette.primary?.[100]};
      }`,
    },
    {
      className: "unavailable",
      selector: (date: Date) =>
        !isDateAvailable(date) &&
        dayjs(date).isAfter(convertDate(today, timeZone)),
      css: `.unavailable {
        color: ${theme.palette.grey?.[900]};
      }`,
    },
  ];

  const modifiers = modifierArr.reduce(
    (acc, { className, selector }) => ({
      [className]: selector,
      ...acc,
    }),
    {},
  );
  const modifiersClassNames = modifierArr.reduce(
    (acc, { className }) => ({
      [className]: className,
      ...acc,
    }),
    {},
  );
  const modifiersCss = modifierArr.reduce(
    (acc, { css }) => acc + "\n" + css,
    "",
  );

  const usesMultipleWeekSelection =
    numWeeksToRepeat !== undefined && numWeeksToRepeat !== 1;
  const css = getCSSScript(theme, modifiersCss, usesMultipleWeekSelection);

  return (
    <Box
      sx={(theme: Theme) => ({
        borderRadius: "1em",
        width: "100%",
        padding: 16,
        gridArea: "date-selector",
        height: "100%",
        overflow: "auto",
        backgroundColor: "neutral.50",
        [theme.breakpoints.up("md")]: {
          width: 316,
        },
      })}
    >
      <DayPickerLocalized
        selected={computedSelectedDates}
        locale={locale}
        today={convertDate(firstAvailableDate ?? today, timeZone)}
        defaultMonth={
          defaultMonth ? convertDate(defaultMonth, timeZone) : undefined
        }
        showOutsideDays
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        onDayClick={(date: Date) =>
          handleDayClick(convertDate(date, timeZone, true))
        }
        onMonthChange={(date: Date) => {
          if (!handleMonthChange) {
            return;
          }

          handleMonthChange(convertDate(date, timeZone, true));
        }}
        fromMonth={today}
        toMonth={lastAvailableDate}
        components={{
          CaptionLabel: CustomCaptionLabel,
        }}
      />
      <style>{css}</style>
    </Box>
  );
}

// fixes issue with incorrect a11y heading
function CustomCaptionLabel(props: CaptionLabelProps): JSX.Element {
  const {
    locale,
    classNames,
    styles,
    formatters: { formatCaption },
  } = useDayPicker();
  const theme: Theme = useTheme();
  return (
    <h5
      className={classNames.caption_label}
      style={{
        ...styles.caption_label,
        fontSize: theme.typography.h5.fontSize as any,
        fontWeight: 500,
      }}
      aria-live={"polite"}
      aria-atomic={"true"}
      id={props.id}
    >
      {formatCaption(props.displayMonth, { locale })}
    </h5>
  );
}
